import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useFlexLayout, useTable } from 'react-table';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';

import { RESTORE_RESULTS_TABLE } from './constants';
import { messages, header, results } from './messages';
import style from './restore-results-modal.module.less';
import OrderNumberLink from '../../../components/order-number-link';
import Global from '../../../store/global-selectors';

const { headers } = RESTORE_RESULTS_TABLE;
const MODAL_HEADER_ID = 'restore-results-modal-header';

type RestoredOrder = {
  CreatedFromFulfillmentPlanId: string;
  NewFulfillmentPlanId: string;
  Action: keyof typeof results;
};

type RestoreResultsModalProps = {
  data: RestoredOrder[];
  onClose: () => void;
};

export const RestoreResultsModal = ({ data, onClose }: RestoreResultsModalProps) => {
  const intl = useIntl();
  const salesOrdersFulfilled = useSelector((state) =>
    Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, {
      fulfillmentPlanIds: data.map((restoredOrder) => restoredOrder.CreatedFromFulfillmentPlanId),
    }),
  );

  const tableData = useMemo(
    () =>
      data.map((restoredOrder) => {
        const resultText = intl.formatMessage(results[restoredOrder.Action]);

        if (salesOrdersFulfilled.length === 0) return { order: '', result: resultText };

        const orderData = salesOrdersFulfilled.find((order: { fulfillmentPlanIds: string[] }) =>
          order.fulfillmentPlanIds.includes(restoredOrder.CreatedFromFulfillmentPlanId),
        );
        if (!orderData) return { order: '', result: resultText };

        return {
          order: <OrderNumberLink orderNumber={orderData.orderNumber} salesOrderId={orderData.salesOrderId} />,
          result: resultText,
        };
      }),
    [data, intl, salesOrdersFulfilled],
  );

  const columns = useMemo(
    () =>
      headers.map((accessor) => ({
        accessor,
        Header: intl.formatMessage(header[accessor]),
      })),
    [intl],
  );

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable({ columns, data: tableData }, useFlexLayout);

  return (
    <Modal className={style['restore-results-modal']} modalHeaderId={MODAL_HEADER_ID} size='medium'>
      <ModalHeader className={style['restore-results-modal-header']} closeModal={onClose} id={MODAL_HEADER_ID}>
        <FormattedMessage {...messages.modalTitle} />
      </ModalHeader>
      <ModalBody className={style['restore-results-modal-body']}>
        <div className={style['restore-results-body-content']}>
          <div className={style['helper-text']}>
            <FormattedMessage {...messages.helperText} />
          </div>
          <ReactTableWrapper
            getTableBodyProps={getTableBodyProps}
            getTableProps={getTableProps}
            headerGroups={headerGroups}
            prepareRow={prepareRow}
            rows={rows}
          />
        </div>
      </ModalBody>
      <ModalFooter className={style['restore-results-modal-footer']}>
        <Button className={style['close-button']} onClick={onClose} theme='primary'>
          <FormattedMessage {...messages.closeButton} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};
